import { useMemo } from 'react';
import { MessageSquare } from 'lucide-react';
import type { ProMessageThreadForPro } from '../lib/api';

interface MessagesThreadListProps {
  threads: ProMessageThreadForPro[];
  selectedPatientId: string | null;
  onSelect: (patientId: string) => void;
  isLoading: boolean;
  isError: boolean;
}

// Intl.RelativeTimeFormat IT (singleton riusato per perf).
const RTF = new Intl.RelativeTimeFormat('it', { numeric: 'auto' });

function relativeTime(iso: string): string {
  // iso può essere "YYYY-MM-DD HH:MM:SS" (UTC da datetime('now')) o full ISO.
  // Normalizziamo aggiungendo 'Z' se mancante per consistency UTC.
  const normalized = iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z';
  const then = new Date(normalized).getTime();
  if (Number.isNaN(then)) return iso;
  const diffSec = (then - Date.now()) / 1000;
  const abs = Math.abs(diffSec);
  if (abs < 60) return RTF.format(Math.round(diffSec), 'second');
  if (abs < 3600) return RTF.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return RTF.format(Math.round(diffSec / 3600), 'hour');
  if (abs < 86400 * 7) return RTF.format(Math.round(diffSec / 86400), 'day');
  if (abs < 86400 * 30) return RTF.format(Math.round(diffSec / (86400 * 7)), 'week');
  return RTF.format(Math.round(diffSec / (86400 * 30)), 'month');
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function truncate(s: string, n: number): string {
  if (!s) return '';
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + '…';
}

export function MessagesThreadList({
  threads,
  selectedPatientId,
  onSelect,
  isLoading,
  isError,
}: MessagesThreadListProps) {
  const sortedThreads = useMemo(
    () =>
      [...threads].sort((a, b) => {
        // unread prima, poi DESC per last_at
        if (a.unread_count > 0 !== b.unread_count > 0) {
          return a.unread_count > 0 ? -1 : 1;
        }
        return (b.last_at || '').localeCompare(a.last_at || '');
      }),
    [threads]
  );

  return (
    <aside
      aria-label="Lista conversazioni"
      style={{
        width: 320,
        flex: '0 0 320px',
        borderRight: '1px solid var(--line)',
        background: 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          padding: '20px 18px 14px',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <MessageSquare size={18} strokeWidth={1.8} style={{ color: 'var(--ink)' }} />
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 1.4,
              color: 'var(--mute)',
              textTransform: 'uppercase',
            }}
          >
            Studio · Chat
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: 'var(--display)',
              fontWeight: 800,
              fontSize: 20,
              letterSpacing: -0.4,
              color: 'var(--ink)',
              lineHeight: 1.1,
            }}
          >
            Conversazioni
          </h2>
        </div>
        {threads.length > 0 && (
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--mute)',
              fontVariantNumeric: 'tabular-nums',
            }}
            aria-label={`${threads.length} conversazioni`}
          >
            {threads.length}
          </span>
        )}
      </header>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {isLoading && (
          <div style={{ padding: 24, color: 'var(--mute)', fontSize: 13 }}>
            Caricamento…
          </div>
        )}
        {isError && (
          <div style={{ padding: 24, color: 'var(--red)', fontSize: 13 }}>
            Errore di caricamento. Riprova.
          </div>
        )}
        {!isLoading && !isError && sortedThreads.length === 0 && (
          <div
            style={{
              padding: '32px 24px',
              color: 'var(--mute)',
              fontSize: 13,
              lineHeight: 1.5,
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 10 }} aria-hidden>
              💬
            </div>
            Nessuna conversazione attiva. Apri una scheda paziente per iniziare a scrivere.
          </div>
        )}
        {!isLoading &&
          !isError &&
          sortedThreads.map((t) => {
            const isActive = t.patient_id === selectedPatientId;
            const hasUnread = t.unread_count > 0;
            return (
              <button
                key={t.patient_id}
                type="button"
                onClick={() => onSelect(t.patient_id)}
                aria-current={isActive ? 'true' : undefined}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'stretch',
                  gap: 12,
                  padding: '14px 16px',
                  background: isActive ? 'var(--green-soft)' : 'transparent',
                  border: 'none',
                  borderBottom: '1px solid var(--line-3)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'var(--sans)',
                  position: 'relative',
                }}
              >
                <div
                  aria-hidden
                  style={{
                    flex: '0 0 40px',
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: isActive ? 'var(--green)' : 'var(--ink)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: 13,
                    letterSpacing: 0.5,
                  }}
                >
                  {initials(t.patient_name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: hasUnread ? 800 : 700,
                        fontSize: 14,
                        color: 'var(--ink)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t.patient_name}
                    </span>
                    <span
                      style={{
                        fontFamily: 'var(--mono)',
                        fontSize: 10.5,
                        color: 'var(--mute)',
                        flex: '0 0 auto',
                      }}
                    >
                      {relativeTime(t.last_at)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginTop: 3,
                    }}
                  >
                    {t.patient_human_id && (
                      <span
                        style={{
                          fontFamily: 'var(--mono)',
                          fontSize: 10,
                          color: 'var(--mute)',
                          background: 'var(--line-3)',
                          padding: '1px 5px',
                          borderRadius: 4,
                          letterSpacing: 0.5,
                        }}
                      >
                        {t.patient_human_id}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 12.5,
                      color: hasUnread ? 'var(--ink-2)' : 'var(--mute)',
                      fontWeight: hasUnread ? 600 : 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.4,
                    }}
                  >
                    {truncate(t.last_msg, 50)}
                  </div>
                </div>
                {hasUnread && (
                  <span
                    aria-label={`${t.unread_count} non letti`}
                    style={{
                      position: 'absolute',
                      top: 14,
                      right: 14,
                      minWidth: 18,
                      height: 18,
                      padding: '0 5px',
                      borderRadius: 9,
                      background: 'var(--orange)',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      letterSpacing: 0.3,
                    }}
                  >
                    {t.unread_count > 9 ? '9+' : t.unread_count}
                  </span>
                )}
              </button>
            );
          })}
      </div>
    </aside>
  );
}
